import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, notificationApi } from '../api/jobApi';
import ToastNotification from '../components/ToastNotification';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toastData, setToastData] = useState(null);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await authApi.getMe();
      setUser(res.data);
    } catch {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    notificationApi.getUnreadCount()
      .then(res => setUnreadCount(res.data))
      .catch(err => console.error('Failed to get unread count:', err));

    const sse = new EventSource(`/api/notifications/subscribe?token=${token}`);

    sse.addEventListener('NOTIFICATION', (e) => {
      try {
        const data = JSON.parse(e.data);
        setUnreadCount(prev => prev + 1);
        setToastData({ message: data.message, jobPostId: data.jobPostId });

        setTimeout(() => setToastData(null), 5000); // 5초 후 자동 닫기
      } catch (err) {
        console.error('Failed to parse SSE notification', err);
      }
    });

    sse.onerror = (e) => {
      // connection logic handles reconnects, just log for debugging
      console.log('SSE connection issue or closed');
    };

    return () => {
      sse.close();
    };
  }, [user]);

  const login = (token) => {
    localStorage.setItem('token', token);
    fetchUser();
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, fetchUser, unreadCount, setUnreadCount }}>
      {children}
      {toastData && (
        <ToastNotification
          message={toastData.message}
          onClick={() => {
            if (toastData.jobPostId) {
              window.location.href = `/jobs/${toastData.jobPostId}`;
            }
          }}
          onClose={() => setToastData(null)}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
